import React, { useMemo } from 'react'
import { withStyles, makeStyles } from '@material-ui/core/styles'
import {
  Theme,
  Link,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import MuiDialogContent from '@material-ui/core/DialogContent'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import Typography from '@material-ui/core/Typography'
import { FormattedMessage } from 'react-intl'
import messages from '@src/routes/Configurators/messages'
import { TubeProduct } from '@src/routes/Configurators/products/TubesMTORedesign/enum'

const styles = makeStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: 0,
  },
  rootRow: {
    background: '#F8F8FC',
    fontWeight: theme.typography.fontWeightMedium,
    borderBottom: 0,
    padding: theme.spacing(2, 1),
  },
  tableBody: {
    padding: theme.spacing(2, 1),
  },
  table: {
    minWidth: 850,
    margin: theme.spacing(6, 0),
    '& td:first-child': {
      paddingLeft: theme.spacing(6),
    },
    '& th:first-child': {
      paddingLeft: theme.spacing(6),
    },
  },
  dialog: {
    textTransform: 'uppercase',
    position: 'relative',
  },
  label: {
    fontWeight: 'normal',
    fontSize: theme.typography.pxToRem(14),
    marginBottom: theme.spacing(2),
  },
  clearButton: {
    position: 'absolute',
    padding: theme.spacing(1),
    right: 0,
    top: 0,
    '& svg': {
      width: theme.typography.pxToRem(24),
      height: theme.typography.pxToRem(24),
      color: theme.palette.text.primary,
    },
  },
  styleLink: {
    color: theme.palette.primary.main,
  },
  container: {
    padding: theme.spacing(8),
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
  },
}))

export interface DialogTitleProps {
  id: string
  children: React.ReactNode
  onClose: () => void
}

const DialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props
  const classes = styles()
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.clearButton}
          onClick={onClose}
          color="primary"
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  )
}

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(0),
  },
}))(MuiDialogContent)

interface ExpectedYieldsProps {
  isValid: boolean
  isModified: boolean
  productType: string
  hasAsterisk: boolean
  linkText?: string
}

const ExpectedYields: React.FC<ExpectedYieldsProps> = (props) => {
  const [open, setOpen] = React.useState(false)
  const { isValid, productType, isModified, linkText, hasAsterisk } = props

  const handleClickOpen = () => {
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }
  const classes = styles()
  const withoutAsterisk = [
    { Scale: '0.025', Desalt: '3/90', Cartridge: 'NA', HPLC: 'NA', Page: 'NA' },
    {
      Scale: '0.05',
      Desalt: '5/150',
      Cartridge: '1/30',
      HPLC: '1/30',
      Page: '0.5/15',
    },
    {
      Scale: '0.2',
      Desalt: '12/360',
      Cartridge: '3/90',
      HPLC: '2.5/75',
      Page: '1/30',
    },
    {
      Scale: '1.0',
      Desalt: '40/1,200',
      Cartridge: '12/360',
      HPLC: '13/390',
      Page: '5/150',
    },
    {
      Scale: '10',
      Desalt: '400/12,000',
      Cartridge: 'NA',
      HPLC: '130/3,900',
      Page: 'NA',
    },
    {
      Scale: '15',
      Desalt: '600/18,000',
      Cartridge: 'NA',
      HPLC: '190/5,700',
      Page: 'NA',
    },
  ]
  const withAsterisk = [
    {
      Scale: '0.05',
      Desalt: '3.5/105',
      Cartridge: '0.7/21',
      HPLC: '0.7/21',
      Page: '0.35/10.5',
    },
    {
      Scale: '0.2',
      Desalt: '	8.4/252',
      Cartridge: '2.1/63',
      HPLC: '1.75/52.5',
      Page: '0.7/21',
    },
    {
      Scale: '1.0',
      Desalt: '28/840',
      Cartridge: '8.4/252',
      HPLC: '9.1/273',
      Page: '3.5/105',
    },
    {
      Scale: '10',
      Desalt: '280/8,400',
      Cartridge: 'NA',
      HPLC: '91/2,730',
      Page: 'NA',
    },
    {
      Scale: '15',
      Desalt: '420/12,600',
      Cartridge: 'NA',
      HPLC: '133/3,990',
      Page: 'NA',
    },
  ]

  const modified = [
    {
      Scale: '0.05',
      Desalt: '2/60',
      Cartridge: '0.4/12',
      HPLC: '0.4/12',
      Page: '0.2/6',
    },
    {
      Scale: '0.2',
      Desalt: '5/150',
      Cartridge: '1/30',
      HPLC: '1/30',
      Page: '0.4/12',
    },
    {
      Scale: '1.0',
      Desalt: '16/480',
      Cartridge: '5/150',
      HPLC: '5/150',
      Page: '2/60',
    },
    {
      Scale: '10',
      Desalt: '160/4,800',
      Cartridge: 'NA',
      HPLC: '52/1,560',
      Page: 'NA',
    },
    {
      Scale: '15',
      Desalt: '240/7,200',
      Cartridge: 'NA',
      HPLC: '76/2,280',
      Page: 'NA',
    },
  ]

  if (productType === TubeProduct.Rna) {
    withoutAsterisk.splice(0, 1)
  }
  const tableData = isModified
    ? modified
    : hasAsterisk
      ? withAsterisk
      : withoutAsterisk

  //3 types, 1) Normal, 2) with Asteris Insertion, 3) with Modified
  const titleLabel = useMemo(() => {
    if (productType === TubeProduct.Rna) {
      return isModified ? (
        <FormattedMessage
          id="CONF_MODIFIED_RNA_OLIGOS_MODIFIED"
          defaultMessage="Modified RNA Oligos (OD/µg)*"
        />
      ) : (
        <FormattedMessage
          id="CONF_NON_MODIFIED_RNA_OLIGOS_MODIFIED"
          defaultMessage="Non-Modified RNA Oligos (OD/μg)*"
        />
      )
    } else {
      return isModified ? (
        <FormattedMessage
          id="CONF_MODIFIED_OLIGOS_MODIFIED"
          defaultMessage="Modified Oligos (OD/μg)*"
        />
      ) : hasAsterisk ? (
        <FormattedMessage
          id="SPEC_NON_MODIFIED_OLIGOS_ST_MODIFIED"
          defaultMessage="Non-Modified Phosphorothioates(S-Oligos)(OD/µg)*"
        />
      ) : (
        <FormattedMessage
          id="CONF_NON_MODIFIED_OLIGOS_MODIFIED"
          defaultMessage="Non-Modified Oligos (OD/μg)*"
        />
      )
    }
  }, [isModified, hasAsterisk])
  const footerMessage =
    productType === TubeProduct.Rna ? (
      isModified ? (
        <FormattedMessage {...messages._PC_SPEC_MODIFIES_RNA_SCALE} />
      ) : (
        <FormattedMessage {...messages.CONF_RNA_EXPECTED_YIELDS_FOOTER_TEXT} />
      )
    ) : isModified ? (
      <FormattedMessage
        id="CONF_EXPECTED_YIELDS_FOOTER_TEXT_MODIFIED"
        defaultMessage="*Guarantee is for 20 bases or longer. Shorter oligos may have fewer ODs. Microgram amounts are calculated based on a 30 µg/OD value. If the µg/OD value is less than 30, the reported µg value will be proportionally less. Note: Post-synthesis modifications may yield 50% less than the above stated values. Phosphorothioated yields are 70% of the above stated guarantees."
      />
    ) : (
      <FormattedMessage
        id="CONF_EXPECTED_YIELDS_FOOTER_TEXT_NON_MODIFIED"
        defaultMessage="*Guarantee is for 20 bases or longer.  Shorter oligos may have fewer ODs."
      />
    )

  return (
    <div>
      <Link
        component="button"
        underline={isValid ? 'none' : 'hover'}
        disabled={isValid}
        onClick={handleClickOpen}
        className={classes.styleLink}
      >
        {linkText ? (
          linkText
        ) : (
          <i>
            {' '}
            <FormattedMessage {...messages.CONF_YIELDS_TABLE} />{' '}
          </i>
        )}
      </Link>

      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth="md"
        classes={{ paper: classes.container }}
      >
        <div className={classes.dialog}>
          <DialogTitle id="customized-dialog-title" onClose={handleClose}>
            <b>
              <FormattedMessage
                id="CONF_EXPECTED_YIELDS_HEADER_TEXT_MODIFIED"
                defaultMessage="synthesis scale expected yields"
              />
            </b>
          </DialogTitle>
        </div>
        <div className={classes.subTitle}>{titleLabel}</div>

        <DialogContent>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.CONF_SCALE} />}
                </TableCell>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.CONF_DESALT} />}
                </TableCell>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.CONF_CARTRIDGE} />}
                </TableCell>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.HPLC} />}
                </TableCell>
                <TableCell align="left" className={classes.rootRow}>
                  {<FormattedMessage {...messages.CONF_POPUP_PAGE} />}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tableData.map((data, index) => (
                <TableRow key={index}>
                  <TableCell align="left" className={classes.tableBody}>
                    {data.Scale}
                  </TableCell>
                  <TableCell align="left" className={classes.tableBody}>
                    {data.Desalt}
                  </TableCell>
                  <TableCell align="left" className={classes.tableBody}>
                    {data.Cartridge}
                  </TableCell>
                  <TableCell align="left" className={classes.tableBody}>
                    {data.HPLC}
                  </TableCell>
                  <TableCell align="left" className={classes.tableBody}>
                    {data.Page}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div>{footerMessage}</div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ExpectedYields
